import { Dispatch } from 'redux';
import Releases from '../../services/Releases';
import { Release, Change } from '../../models';
import { createRestSlices, applyReducers } from './rest';
import { ActionPayload } from '..';
import { upsertList } from './remoteUpdate';

const {
    initialState,
    createReducer, startCreateReducer, cancelCreateReducer,
    getReducer, listReducer,
    updateReducer, deleteReducer,
    duplicateReducer,
    createAction, startCreateAction, cancelCreateAction,
    getAction, listAction,
    updateAction, deleteAction,
    duplicateAction,
} = createRestSlices(Releases);

const UPDATE = 'yoda/mercure/REMOTE_UPDATE';
const onRemoteUpdateReducer = (state = initialState, action: ActionPayload) => {
    if (action.type !== UPDATE)
        return state;

    const { update: { data, type }} = action;
    switch (type) {
        case 'release.created':
            return {
                ...state,
                releases: upsertList(state.releases, data, (payload) => new Release(payload), true)
            };
        case 'release.updated':
            return {
                ...state,
                releases: upsertList(state.releases, data, (payload) => new Release(payload)),
                release: state.release?._id === data._id ? new Release(data) : state.release
            };
        case 'change.updated':
            if (!state.releases?.length)
                return state;

            // let's replace the original change in the state release
            // with the updated one
            const releases = (state.releases || []).map((release: Release) => {
                release.changes = release.changes.map((original: Change) => {
                    return original._id === data._id ? new Change(data) : original;
                });
                return release;
            });
            return {
                ...state,
                releases
            };
        default:
            return state;
    }
};

/**
 * We have to add this hook to handle modifications on changes when we are
 * looking at a specific release...
 */
const UPDATING_CHANGE_SUCCESS = 'yoda/change/UPDATING_ITEM_SUCCESS';
const updateChangeReducer = (state = initialState, action: ActionPayload) => {
    if (!state.releases?.length)
        return state;

    switch (action.type) {
        case UPDATING_CHANGE_SUCCESS:
            const { change } = action;

            // let's replace the original change in the state release
            // with the updated one
            const releases = (state.releases || []).map((release: Release) => {
                release.changes = release.changes.map((original: Change) => {
                    return original._id !== change._id ? original : new Change({
                        ...original,
                        ...change
                    });
                });
                return release;
            });
            return {
                ...state,
                releases
            };
        default:
            return state;
    }
};

/* Reorder changes within a release */
const REORDER_CHANGES         = 'yoda/release/REORDER_CHANGES';
const REORDER_CHANGES_SUCCESS = 'yoda/release/REORDER_CHANGES_SUCCESS';
const REORDER_CHANGES_FAILURE = 'yoda/release/REORDER_CHANGES_FAILURE';

const reorderChangesReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case REORDER_CHANGES_SUCCESS:
            if (!state.releases?.length)
                return state;
            return {
                ...state,
                releases: state.releases.map((r: Release) =>
                    r._id === action.release._id ? action.release : r
                ),
            };
        default:
            return state;
    }
};

const reorderChangesAction = (releaseId: string, changeIds: string[]) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: REORDER_CHANGES });
        return Releases.reorderChanges(releaseId, changeIds)
            .then(json => {
                dispatch({ type: REORDER_CHANGES_SUCCESS, release: json.release });
                return json;
            })
            .catch((err: Error) => {
                dispatch({ type: REORDER_CHANGES_FAILURE, error: err.message });
                return null;
            });
    };
};

/* Export reducer */
/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (state = initialState, action: ActionPayload) => {
    return applyReducers(state, action, [
        createReducer, startCreateReducer, cancelCreateReducer,
        getReducer, listReducer,
        updateReducer, deleteReducer, duplicateReducer,
        onRemoteUpdateReducer, updateChangeReducer, reorderChangesReducer
    ]);
}

/* Export CRUD actions */
export const createRelease         = createAction;
export const startCreateRelease    = startCreateAction;
export const cancelCreateRelease   = cancelCreateAction;
export const loadRelease           = getAction;
export const loadReleases          = listAction;
export const updateRelease         = updateAction;
export const deleteRelease         = deleteAction;
export const duplicateRelease      = duplicateAction;
export const reorderReleaseChanges = reorderChangesAction;
